import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import WhatTypeOfPlaceDoYouPrefer from '$/_const/dna/WhatTypeOfPlaceDoYouPrefer.ts';
import type { WhatTypeOfPlaceDoYouPrefer as StayOption } from '$/_const/dna/WhatTypeOfPlaceDoYouPrefer.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaImageChoiceCard from '@/components/functional/TravelDnaManipulationForm/shared/DnaImageChoiceCard.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

import smallBoutiqueImage from '@/assets/img/onboarding/stay/small-boutique.jpg';
import cityHotelImage from '@/assets/img/onboarding/stay/city-hotel.jpg';
import resortImage from '@/assets/img/onboarding/stay/resort.jpg';
import homeOrApartmentImage from '@/assets/img/onboarding/stay/home-or-apartment.jpg';

const yourStaySchema = dnaSchema.pick({
  what_type_of_place_do_you_prefer: true,
});

export type YourStaySchema = z.infer<typeof yourStaySchema>;

type YourStayProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const stayOptions: StayOption[] = [
  WhatTypeOfPlaceDoYouPrefer.BOUTIQUE_SMALL_CHARACTERFUL,
  WhatTypeOfPlaceDoYouPrefer.DESIGN_HOTEL_STYLISH_MODERN,
  WhatTypeOfPlaceDoYouPrefer.RESORT_SPACE_FACILITIES,
  WhatTypeOfPlaceDoYouPrefer.HOME_OR_APARTMENT,
];

const stayImages: Record<StayOption, string> = {
  boutique_small_characterful: smallBoutiqueImage,
  design_hotel_stylish_modern: cityHotelImage,
  resort_space_facilities: resortImage,
  home_or_apartment: homeOrApartmentImage,
  large_chain_reliable: cityHotelImage,
  no_strong_preference: smallBoutiqueImage,
};

function YourStay({ onComplete, onCompleteWhenDirty }: YourStayProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const selectedValue = contextData?.what_type_of_place_do_you_prefer;

  const selectOption = (option: StayOption) => {
    void (async () => {
      try {
        if (isPending) return;
        setIsPending(true);
        setError(null);

        const data = yourStaySchema.parse({
          what_type_of_place_do_you_prefer: option,
        });
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_STAY,
          data,
          context,
        };

        if (selectedValue !== option) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourStay.header')}
      title={t('travelDnaPage.yourStay.subheader')}
      description={t('travelDnaPage.yourStay.description')}
    >
      <DnaError error={error} className="mt-6" />
      <div
        className="mt-6 grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-7"
        role="radiogroup"
        aria-label={t('travelDnaPage.yourStay.subheader')}
      >
        {stayOptions.map((option) => {
          const selected = selectedValue === option;
          return (
            <DnaImageChoiceCard
              key={option}
              image={stayImages[option]}
              label={t(
                `travelDnaPage.yourStay.options.${option}`,
                missingTranslationToReadable(option)
              )}
              selected={selected}
              dimmed={Boolean(selectedValue) && !selected}
              disabled={isPending}
              onClick={() => selectOption(option)}
            />
          );
        })}
      </div>
    </DnaStepLayout>
  );
}

export default YourStay;
